/**
 * BookingFilters — composes the status and source filters, search and sort
 * controls (BON-130, source BON-649).
 *
 * Status is chips on mobile/tablet and a select on desktop; source is a select
 * on every breakpoint (the mock's Region/Venue/Workshop/date dropdowns are
 * still deferred). Search renders on every breakpoint. Sort: desktop sorts via
 * the table's clickable column headers, so the compact sort control renders
 * ONLY on the card breakpoints. All drive the list query params
 * (status | source | q | sort | dir).
 *
 * The three controls live in their own files — one component per file — and
 * their shared option lists in `bookingFilterOptions`.
 */
import { Search } from 'lucide-react';

import type { BookingSort, BookingSortDir } from '../../api/bookingsList';

import { type SourceValue, type StatusValue } from './bookingFilterOptions';
import { BookingSortControl } from './BookingSortControl';
import { BookingSourceSelect } from './BookingSourceSelect';
import { BookingStatusChips } from './BookingStatusChips';
import { BookingStatusSelect } from './BookingStatusSelect';

export type { SourceValue, StatusValue };

interface Props {
  status: StatusValue;
  onStatusChange: (v: StatusValue) => void;
  source: SourceValue;
  onSourceChange: (v: SourceValue) => void;
  search: string;
  onSearchChange: (v: string) => void;
  sort: BookingSort;
  dir: BookingSortDir;
  onSortChange: (v: BookingSort) => void;
  onDirToggle: () => void;
  isDesktop: boolean;
}

export function BookingFilters({
  status,
  onStatusChange,
  source,
  onSourceChange,
  search,
  onSearchChange,
  sort,
  dir,
  onSortChange,
  onDirToggle,
  isDesktop,
}: Props) {
  return (
    <div className="mb-4 space-y-3">
      {/* Status: chips on mobile/tablet, a select on desktop. Desktop sorting
          lives in the table's column headers, so no sort control here. */}
      <div className="flex flex-wrap items-center gap-3">
        {isDesktop ? (
          <BookingStatusSelect status={status} onStatusChange={onStatusChange} />
        ) : (
          <BookingStatusChips status={status} onStatusChange={onStatusChange} />
        )}
        <BookingSourceSelect source={source} onSourceChange={onSourceChange} />
      </div>

      {/* Search (all breakpoints) + sort (non-desktop lives beside search) */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <input
            type="text"
            aria-label="Search bookings by name, email, or reference"
            placeholder="Search name, email, ref…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-stone-200 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:border-stone-700 dark:bg-[#1e2520] dark:text-[#e8ece9]"
          />
        </div>
        {!isDesktop && (
          <BookingSortControl
            sort={sort}
            dir={dir}
            onSortChange={onSortChange}
            onDirToggle={onDirToggle}
          />
        )}
      </div>
    </div>
  );
}
